import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, TextField, Button, Tooltip, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, CircularProgress, Autocomplete,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { supplierOffersAPI } from '../../services/api';
import downloadFile from '../../services/downloadFile';

const fmt = (v) => (v === null || v === undefined
  ? '-'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/**
 * Partidas de una oferta de proveedor.
 *
 * Es donde se recibe la oferta: las partidas ya estan creadas desde la peticion,
 * asi que aqui solo se escriben importes. Las que el proveedor no cotiza se
 * marcan como tales (distinto de dejarlas en blanco), y las que trae de mas se
 * anaden como partidas extra.
 */
export default function OfferLinesEditor({ offer, equipments = [], onChanged }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prices, setPrices] = useState({});      // edicion en curso, por linea
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ equipment_id: '', description: '', qty: 1, unit_price: '' });
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitForm, setSplitForm] = useState({ equipment_ids: [], total_amount: '', label: '' });
  const [info, setInfo] = useState('');
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [orderConfirm, setOrderConfirm] = useState(null);

  const readOnly = offer?.status === 'superseded';

  const load = useCallback(async () => {
    if (!offer?.id) return;
    setLoading(true);
    try {
      const resp = await supplierOffersAPI.listLines(offer.id);
      setLines(resp.data);
      setPrices(Object.fromEntries(resp.data.map((l) => [l.id, l.unit_price ?? ''])));
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se han podido cargar las partidas.');
    } finally {
      setLoading(false);
    }
  }, [offer?.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    try {
      setError('');
      const resp = await fn();
      if (resp?.data?.message) setInfo(resp.data.message);
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido guardar el cambio.');
    }
  };

  // sin decidir -> adjudicada -> descartada -> sin decidir
  const cycleAward = (line) => {
    const next = line.is_awarded === true ? false : line.is_awarded === false ? null : true;
    run(() => supplierOffersAPI.awardLine(line.id, next));
  };

  const downloadRequest = async () => {
    try {
      setError('');
      const resp = await supplierOffersAPI.requestDocument(offer.id);
      const name = downloadFile(resp, 'peticion_oferta.docx');
      setInfo(`Descargado ${name}. Abrelo en Word para retocarlo antes de enviarlo.`);
    } catch (err) {
      setError('No se ha podido generar el documento.');
    }
  };

  const createOrder = async (force = false) => {
    try {
      setError('');
      const resp = await supplierOffersAPI.createOrder(offer.id, force ? { force: true } : {});
      setInfo(resp.data.message);
      setOrderConfirm(null);
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se ha podido generar el pedido.';
      if (err.response?.status === 409 && detail.includes('sin adjudicar')) {
        setOrderConfirm(detail);
      } else {
        setError(detail);
      }
    }
  };

  const savePrice = (line) => {
    const raw = prices[line.id];
    const value = raw === '' || raw === null ? null : Number(raw);
    if (value === (line.unit_price ?? null)) return;
    if (raw !== '' && Number.isNaN(value)) {
      setError('El precio tiene que ser un numero.');
      setPrices({ ...prices, [line.id]: line.unit_price ?? '' });
      return;
    }
    run(() => supplierOffersAPI.updateLine(line.id, { unit_price: value }));
  };

  const toggleNotQuoted = (line) =>
    run(() => supplierOffersAPI.updateLine(line.id, { not_quoted: !line.not_quoted }));

  const handleAdd = () => run(async () => {
    const eq = equipments.find((e) => e.id === addForm.equipment_id);
    await supplierOffersAPI.createLine(offer.id, {
      equipment_id: addForm.equipment_id || null,
      description: addForm.description || (eq ? eq.name : ''),
      qty: addForm.qty || 1,
      unit_price: addForm.unit_price === '' ? null : Number(addForm.unit_price),
    });
    setAddOpen(false);
    setAddForm({ equipment_id: '', description: '', qty: 1, unit_price: '' });
  });

  const handleSplit = () => run(async () => {
    await supplierOffersAPI.splitGroup(offer.id, {
      equipment_ids: splitForm.equipment_ids,
      total_amount: splitForm.total_amount,
      label: splitForm.label || null,
    });
    setSplitOpen(false);
    setSplitForm({ equipment_ids: [], total_amount: '', label: '' });
  });

  const equipmentTotal = lines
    .filter((l) => l.equipment_id && !l.not_quoted && l.amount !== null)
    .reduce((s, l) => s + l.amount, 0);
  const extrasTotal = lines
    .filter((l) => !l.equipment_id && !l.not_quoted && l.amount !== null)
    .reduce((s, l) => s + l.amount, 0);
  const pending = lines.filter((l) => !l.not_quoted && l.amount === null).length;
  const changed = lines.filter((l) => l.spec_changed).length;
  const alreadyLinked = new Set(lines.map((l) => l.equipment_id).filter(Boolean));
  const awardedCount = lines.filter((l) => l.is_awarded === true).length;
  const discarded = lines.filter((l) => l.is_awarded === false);
  const visibleLines = showDiscarded ? lines : lines.filter((l) => l.is_awarded !== false);
  const awardedTotal = lines
    .filter((l) => l.is_awarded === true && l.amount !== null)
    .reduce((s, l) => s + l.amount, 0);

  if (!offer?.id) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="bold">Partidas de la oferta</Typography>
          {pending > 0 && (
            <Chip size="small" color="warning" label={`${pending} sin precio`} />
          )}
          {changed > 0 && (
            <Chip size="small" color="error" variant="outlined"
              label={`${changed} equipo(s) han cambiado desde que se pidio`} />
          )}
          {awardedCount > 0 && (
            <Chip size="small" color="success" icon={<CheckCircleIcon />}
              label={`${awardedCount} adjudicadas · ${fmt(awardedTotal)} EUR`} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<DescriptionIcon />} onClick={downloadRequest}>
            Peticion en Word
          </Button>
          {!readOnly && (
            <>
              <Button size="small" startIcon={<ReplayIcon />}
                onClick={() => run(() => supplierOffersAPI.revise(offer.id))}>
                Segunda vuelta
              </Button>
              <Button size="small" startIcon={<CallSplitIcon />} onClick={() => setSplitOpen(true)}>
                Precio conjunto
              </Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAddOpen(true)}>
                Anadir partida
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!readOnly && lines.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleIcon />}
            onClick={() => run(() => supplierOffersAPI.awardAll(offer.id))}>
            Adjudicar toda la oferta
          </Button>
          {awardedCount > 0 && (
            <Button size="small" variant="contained" startIcon={<ShoppingCartIcon />}
              onClick={() => createOrder()}>
              Generar pedido
            </Button>
          )}
          {discarded.length > 0 && (
            <Button size="small" onClick={() => setShowDiscarded((v) => !v)}>
              {showDiscarded ? 'Ocultar' : 'Ver'} {discarded.length} partidas descartadas
            </Button>
          )}
        </Box>
      )}

      {readOnly && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Esta es la revision {offer.revision || 1}, sustituida por una posterior. Se conserva como
          historico de la negociacion y no se puede modificar.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setInfo('')}>{info}</Alert>}
      {offer.pricing_mode === 'lump' && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Oferta a tanto alzado: el importe es global y no se reparte por partidas.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {['Tag', 'Partida', 'Cant.', 'Precio ud.', 'Importe', 'Adj.', ''].map((h, i) => (
                  <TableCell key={h || i}
                    align={['Cant.', 'Precio ud.', 'Importe'].includes(h) ? 'right' : 'left'}
                    sx={{ color: '#fff', fontWeight: 'bold', width: h === '' ? 90 : undefined }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      Esta oferta no tiene partidas. Anade las que traiga el proveedor.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleLines.map((line) => (
                <TableRow key={line.id} hover
                  sx={
                    line.is_awarded === true ? { backgroundColor: '#e8f5e9' }
                      : line.is_awarded === false ? { opacity: 0.5 }
                        : line.not_quoted ? { backgroundColor: '#fafafa', opacity: 0.75 }
                          : undefined
                  }>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {line.equipment_tag || (
                      <Chip size="small" label="extra" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{line.description}</Typography>
                      {line.spec_changed && (
                        <Tooltip title={`Se pidio con: ${line.spec_snapshot}. El equipo ha cambiado desde entonces.`}>
                          <WarningAmberIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                      {line.group_label && (
                        <Chip size="small" label={line.group_label} variant="outlined"
                          sx={{ fontSize: '0.62rem' }} />
                      )}
                      {line.is_locked && (
                        <Chip size="small" color="success" label="en pedido" sx={{ fontSize: '0.62rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{line.qty ?? 1}</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>
                    {line.not_quoted ? (
                      <Typography variant="caption" color="textSecondary">no cotiza</Typography>
                    ) : (
                      <TextField
                        size="small" variant="standard" type="number"
                        value={prices[line.id] ?? ''}
                        disabled={readOnly || line.is_locked}
                        onChange={(e) => setPrices({ ...prices, [line.id]: e.target.value })}
                        onBlur={() => savePrice(line)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        inputProps={{ style: { textAlign: 'right' } }}
                        sx={{ width: 110 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {line.not_quoted ? '—' : fmt(line.amount)}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>
                    {line.is_locked ? (
                      <Tooltip title="Ya esta en un pedido: no se puede cambiar">
                        <LockIcon fontSize="small" color="action" />
                      </Tooltip>
                    ) : line.not_quoted || readOnly ? null : (
                      <Tooltip title={
                        line.is_awarded === true ? 'Adjudicada. Pulsa para descartarla'
                          : line.is_awarded === false ? 'Descartada. Pulsa para dejarla sin decidir'
                            : 'Sin decidir. Pulsa para adjudicarla'
                      }>
                        <IconButton size="small" onClick={() => cycleAward(line)}
                          color={line.is_awarded === true ? 'success' : 'default'}>
                          {line.is_awarded === true ? <CheckCircleIcon fontSize="small" />
                            : line.is_awarded === false ? <RemoveCircleOutlineIcon fontSize="small" />
                              : <RadioButtonUncheckedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!readOnly && !line.is_locked && (
                      <>
                        <Tooltip title={line.not_quoted ? 'El proveedor si la cotiza' : 'El proveedor no cotiza esta partida'}>
                          <IconButton size="small" color={line.not_quoted ? 'warning' : 'default'}
                            onClick={() => toggleNotQuoted(line)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!line.equipment_id && (
                          <Tooltip title="Es una maquina: subirla a la lista de equipos">
                            <IconButton size="small" color="primary"
                              onClick={() => run(() => supplierOffersAPI.promoteLine(line.id))}>
                              <UpgradeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Quitar la partida">
                          <IconButton size="small" color="error"
                            onClick={() => run(() => supplierOffersAPI.deleteLine(line.id))}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {lines.length > 0 && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="caption">
                      Equipos {fmt(equipmentTotal)} + Extras {fmt(extrasTotal)} =
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {fmt(equipmentTotal + extrasTotal)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={2} align="right">
                    {awardedCount > 0 && (
                      <Typography variant="caption" color="success.main" fontWeight="bold">
                        adj. {fmt(awardedTotal)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Anadir partida */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Anadir partida a la oferta</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                select fullWidth size="small" label="Equipo del listado (dejar vacio si es una partida extra)"
                value={addForm.equipment_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const eq = equipments.find((x) => x.id === id);
                  setAddForm({ ...addForm, equipment_id: id, description: eq ? eq.name : addForm.description });
                }}
              >
                <MenuItem value=""><em>Partida extra (no esta en el listado)</em></MenuItem>
                {equipments.filter((e) => !alreadyLinked.has(e.id)).map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.tag ? `${e.tag} — ` : ''}{e.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descripcion" value={addForm.description}
                onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Cantidad" value={addForm.qty}
                onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Precio unitario (EUR)"
                value={addForm.unit_price}
                onChange={(e) => setAddForm({ ...addForm, unit_price: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd}
            disabled={!addForm.description && !addForm.equipment_id}>
            Anadir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quedan equipos sin adjudicar */}
      <Dialog open={!!orderConfirm} onClose={() => setOrderConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Quedan equipos sin adjudicar</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{orderConfirm}</Alert>
          <Typography variant="body2">
            El pedido se generara solo con las partidas adjudicadas a este proveedor. El resto
            seguira pendiente de decidir en la comparativa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderConfirm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => createOrder(true)}>Generar igualmente</Button>
        </DialogActions>
      </Dialog>

      {/* Precio conjunto */}
      <Dialog open={splitOpen} onClose={() => setSplitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Repartir un precio conjunto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Para cuando el proveedor cotiza varios equipos con un solo importe
            (&laquo;3 bombas — 12.000 EUR&raquo;). Se reparte a partes iguales y las partidas
            quedan marcadas, para poder compararlas por tag sin perder de donde sale el precio.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple size="small" options={equipments}
                getOptionLabel={(e) => `${e.tag ? e.tag + ' — ' : ''}${e.name}`}
                value={equipments.filter((e) => splitForm.equipment_ids.includes(e.id))}
                onChange={(_, v) => setSplitForm({ ...splitForm, equipment_ids: v.map((e) => e.id) })}
                renderInput={(params) => <TextField {...params} label="Equipos incluidos en el precio" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Importe conjunto (EUR)"
                value={splitForm.total_amount}
                onChange={(e) => setSplitForm({ ...splitForm, total_amount: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Etiqueta (opcional)"
                placeholder="3 bombas conjunto" value={splitForm.label}
                onChange={(e) => setSplitForm({ ...splitForm, label: e.target.value })} />
            </Grid>
            {splitForm.equipment_ids.length > 0 && splitForm.total_amount !== '' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {splitForm.equipment_ids.length} partidas a{' '}
                  {fmt(Number(splitForm.total_amount) / splitForm.equipment_ids.length)} EUR cada una.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSplit}
            disabled={splitForm.equipment_ids.length < 2 || splitForm.total_amount === ''}>
            Repartir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
